import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useIsMobile } from '../../utils/useMediaQuery';
import { Icon } from '../icons/SpriteIcons';

/**
 * Native-app-style bottom tab bar, shown only on phones/tablets (< lg). It is the
 * primary navigation on mobile - the top header collapses to a slim brand bar and
 * this takes over Home / Discover / Tickets / Account.
 *
 * Suppressed on routes that own the bottom of the screen with their own sticky
 * action bar or a focused flow where tabs would be a distraction: the event
 * detail page (sticky buy bar), checkout, auth and the payment result.
 */
const HIDE_ON = [
  /^\/events\/[^/]+$/, // event detail - has its own sticky buy bar
  /^\/checkout/,
  /^\/auth/,
  /^\/payment/,
];

/** Whether the bottom tab bar is shown for a given route (ignores viewport). */
export const tabBarVisibleFor = (pathname: string) => !HIDE_ON.some((re) => re.test(pathname));

interface Tab {
  to: string;
  label: string;
  icon: string;
  /** Extra path prefixes (besides `to`) that should light this tab up. */
  match?: (path: string) => boolean;
}

export const MobileTabBar: React.FC = () => {
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const { isLoggedIn } = useAuth();

  if (!isMobile) return null;
  if (!tabBarVisibleFor(pathname)) return null;

  const tabs: Tab[] = [
    { to: '/', label: 'Home', icon: 'i-home', match: (p) => p === '/' },
    { to: '/events', label: 'Discover', icon: 'i-compass', match: (p) => p.startsWith('/events') },
    { to: '/my-tickets', label: 'Tickets', icon: 'i-ticket', match: (p) => p.startsWith('/my-tickets') },
    isLoggedIn
      ? { to: '/profile', label: 'Account', icon: 'i-user', match: (p) => p.startsWith('/profile') || p.startsWith('/dashboard') }
      : { to: '/auth', label: 'Sign in', icon: 'i-user', match: (p) => p.startsWith('/auth') },
  ];

  return (
    <nav
      aria-label="Primary"
      className="zq-shell fixed inset-x-0 bottom-0 z-[70] lg:hidden border-t border-white/10 bg-[#0c0d14]/95 backdrop-blur-xl"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <ul className="mx-auto flex max-w-md items-stretch justify-around px-2">
        {tabs.map((t) => {
          const active = t.match ? t.match(pathname) : pathname === t.to;
          return (
            <li key={t.label} className="flex-1">
              <Link
                to={t.to}
                aria-current={active ? 'page' : undefined}
                className={`relative flex flex-col items-center gap-1 pb-1.5 pt-2.5 transition-colors ${
                  active ? 'text-primary' : 'text-white/55'
                }`}
              >
                <span
                  className={`relative grid h-7 w-12 place-items-center rounded-full transition-colors ${
                    active ? 'bg-primary/15' : ''
                  }`}
                >
                  <Icon name={t.icon} size={21} />
                </span>
                <span className={`text-[10px] font-semibold tracking-wide ${active ? 'text-primary' : 'text-white/55'}`}>
                  {t.label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
